import { ApolloError } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import { useShowRefreshDashboardModal } from '@modules/dashboard/hooks';
import {
  ListDashboardsDocument,
  useUpdateDashboardMutation,
} from 'generated/graphql';
import { Dashboard } from 'v2/core/entities/dashboard';
import { Scope } from 'v2/core/entities/scope';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

const UPDATE_SEARCH_DASHBOARD_REFETCH_QUERIES = [ListDashboardsDocument];

interface UpdateSearchDashboardOptions {
  searchDashboard: Dashboard;
  onError?: (error: ApolloError) => void;
}

export const useUpdateSearchDashboard = ({
  searchDashboard,
  onError,
}: UpdateSearchDashboardOptions) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const showRefreshDashboardModal = useShowRefreshDashboardModal({
    label: isSearchToFeedEnabled ? 'Feed' : 'Saved Search',
  });

  const [updateSearchDashboardMutation, { loading, error, called }] =
    useUpdateDashboardMutation({
      variables: {
        ...searchDashboard,
        userID,
        tenantID,
        scope: mapToScope(searchDashboard?.scope ?? Scope.PRIVATE),
        editToken: searchDashboard?.editToken ?? '',
        createdBy: userID,
        version: searchDashboard?.version ?? 1,
      },
      refetchQueries: UPDATE_SEARCH_DASHBOARD_REFETCH_QUERIES,
      onError: (error) => {
        if (isErrorCode(error, ErrorCode.UnprocessableEntity)) {
          showRefreshDashboardModal();
          return;
        }

        if (onError) {
          onError(error);
          return;
        }

        showErrorNotification({
          contentComponent: 'Failed to update Search Dashboard',
          props: { isClosable: true },
        });
      },
    });

  const status = apolloStatusToQueryStatus(loading, called, false, error);

  return {
    updateSearchDashboard: updateSearchDashboardMutation,
    isLoading: IsLoading(status),
    error,
  };
};
